import {
    Check,
    Download,
    ExternalLink,
    FileText,
    Image as ImageIcon,
    Link2,
    Mail,
} from 'lucide-react';
import type { ComponentType, CSSProperties } from 'react';
import { Button } from '@archipro/rocco/components/button';
import { cn } from '@rocco/utils/cn';
import { formatFileSize } from '~/modules/business-manager/forms/types';
import type { EnquiryDownloadLink } from '../types/public-enquiry-form';

interface EnquiryDownloadsStepProps {
    downloads: EnquiryDownloadLink[];
    /** Where the same links were emailed, so people know they can come back to them. */
    email: string | null;
    /** The form owner's thank-you copy; falls back to a generic line. */
    message?: string | null;
    /**
     * Shown beneath another step (the booking hand-off) rather than on its own:
     * drops the "Details sent" banner and the closing button.
     */
    compact?: boolean;
    onClose?: () => void;
    className?: string;
}

const DEFAULT_MESSAGE = 'Thanks! Your downloads are ready.';

/** File extension for the type chip, e.g. "PDF". */
const extensionOf = (fileName: string | null): string | null => {
    if (!fileName) return null;
    const dot = fileName.lastIndexOf('.');
    if (dot === -1 || dot === fileName.length - 1) return null;
    return fileName.slice(dot + 1).toUpperCase();
};

const expiryLabel = (downloads: EnquiryDownloadLink[]): string | null => {
    const first = downloads[0]?.expiresAt;
    if (!first) return null;
    const date = new Date(first);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
};

type FileKind = 'pdf' | 'image' | 'link' | 'file';

interface KindStyle {
    /** Short label printed on the badge. */
    label: string;
    Icon: ComponentType<{ className?: string }>;
    /** Badge colours; deliberately not theme-driven so a PDF reads as a PDF on any host page. */
    badge: string;
}

const KIND_STYLE: Record<FileKind, KindStyle> = {
    pdf: {
        label: 'PDF',
        Icon: FileText,
        badge: 'bg-gradient-to-br from-[#f43f5e] to-[#be123c] text-white',
    },
    image: {
        label: 'IMG',
        Icon: ImageIcon,
        badge: 'bg-gradient-to-br from-[#8b5cf6] to-[#5b21b6] text-white',
    },
    link: {
        label: 'LINK',
        Icon: Link2,
        badge: 'bg-gradient-to-br from-[#0ea5e9] to-[#0369a1] text-white',
    },
    file: {
        label: 'FILE',
        Icon: FileText,
        badge: 'bg-gradient-to-br from-[#64748b] to-[#334155] text-white',
    },
};

const kindOf = (download: EnquiryDownloadLink): FileKind => {
    if (download.isExternal) return 'link';
    switch (extensionOf(download.fileName)) {
        case 'PDF':
            return 'pdf';
        case 'JPG':
        case 'JPEG':
        case 'PNG':
        case 'WEBP':
        case 'GIF':
            return 'image';
        default:
            return 'file';
    }
};

/** Type badge shown on the left of each tile: a big glyph over the format name. */
const KindBadge = ({ download }: { download: EnquiryDownloadLink }) => {
    const kind = kindOf(download);
    const { Icon, badge } = KIND_STYLE[kind];
    const label =
        kind === 'file'
            ? (extensionOf(download.fileName) ?? 'FILE')
            : KIND_STYLE[kind].label;
    return (
        <span
            aria-hidden="true"
            className={cn(
                'flex size-14 shrink-0 flex-col items-center justify-center gap-0.5 rounded-[calc(var(--ap-form-radius,0.5rem)+2px)] shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]',
                badge,
            )}
        >
            <Icon className="size-6" />
            <span className="text-[0.6rem] leading-none font-semibold tracking-wide">
                {label.slice(0, 4)}
            </span>
        </span>
    );
};

/**
 * What replaces the form once a gated download form has been sent: the files it
 * offers, each behind a signed link that only this submission can use. Links open
 * in a new tab so the page (or the host page around an embed) stays put.
 */
export const EnquiryDownloadsStep = ({
    downloads,
    email,
    message,
    compact = false,
    onClose,
    className,
}: EnquiryDownloadsStepProps) => {
    const expires = expiryLabel(downloads);
    const count = downloads.length;
    // Tiles start rising once the check has landed.
    const firstTileDelay = compact ? 0 : 320;

    return (
        <div
            className={cn('flex flex-col gap-5', className)}
            role="status"
            aria-live="polite"
        >
            {compact ? null : (
                <div className="flex flex-col items-center gap-3 pt-2 text-center">
                    <span className="relative flex size-16 items-center justify-center">
                        <span className="ap-downloads-ring absolute inset-0 rounded-full bg-[var(--ap-form-accent,#111827)]" />
                        <span className="ap-downloads-pop relative flex size-16 items-center justify-center rounded-full bg-[var(--ap-form-accent,#111827)] text-[var(--ap-form-on-accent,#fff)] shadow-lg shadow-black/10">
                            <Check className="size-8" strokeWidth={2.5} />
                        </span>
                    </span>
                    <div
                        className="ap-downloads-rise flex flex-col gap-1"
                        style={
                            { '--ap-downloads-delay': '160ms' } as CSSProperties
                        }
                    >
                        <h2 className="m-0 text-xl leading-tight font-semibold text-[var(--ap-form-text,#111827)] sm:text-2xl">
                            {count === 1
                                ? 'Your download is ready'
                                : `Your ${count} downloads are ready`}
                        </h2>
                        <p className="m-0 max-w-md text-sm whitespace-pre-wrap text-[var(--ap-form-text,#4b5563)] opacity-80">
                            {message?.trim() || DEFAULT_MESSAGE}
                        </p>
                    </div>
                </div>
            )}

            <ul className="m-0 flex list-none flex-col gap-3 p-0">
                {downloads.map((download, index) => {
                    const extension = download.isExternal
                        ? null
                        : extensionOf(download.fileName);
                    const details = download.isExternal
                        ? 'Opens in a new tab'
                        : [
                              extension,
                              download.fileSize > 0
                                  ? formatFileSize(download.fileSize)
                                  : null,
                          ]
                              .filter(Boolean)
                              .join(' · ');
                    return (
                        <li
                            key={download.id}
                            className="ap-downloads-rise group"
                            style={
                                {
                                    '--ap-downloads-delay': `${firstTileDelay + index * 90}ms`,
                                } as CSSProperties
                            }
                        >
                            <a
                                href={download.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                download={download.isExternal ? undefined : ''}
                                className="flex items-center gap-4 rounded-[var(--ap-form-radius,0.5rem)] border border-[var(--ap-form-field-border,#e5e7eb)] bg-[var(--ap-form-field-bg,#fff)] p-3 no-underline transition-all duration-200 hover:-translate-y-0.5 hover:border-[var(--ap-form-accent,#111827)] hover:shadow-[0_10px_30px_-12px_rgba(0,0,0,0.25)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ap-form-accent,#111827)] sm:p-4"
                            >
                                <KindBadge download={download} />
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-base font-semibold text-[var(--ap-form-text,#111827)]">
                                        {download.title}
                                    </span>
                                    {details ? (
                                        <span className="block text-xs text-[var(--ap-form-text,#6b7280)] opacity-70">
                                            {details}
                                        </span>
                                    ) : null}
                                </span>
                                <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[var(--ap-form-accent,#111827)] px-4 py-2 text-sm font-medium text-[var(--ap-form-on-accent,#fff)] transition-transform duration-200 group-hover:scale-105">
                                    {download.isExternal ? (
                                        <ExternalLink className="size-4" />
                                    ) : (
                                        <Download className="size-4 transition-transform duration-200 group-hover:translate-y-0.5" />
                                    )}
                                    {download.isExternal ? 'Open' : 'Download'}
                                </span>
                            </a>
                        </li>
                    );
                })}
            </ul>

            <div
                className="ap-downloads-rise flex items-start gap-2.5 rounded-[var(--ap-form-radius,0.5rem)] border border-dashed border-[var(--ap-form-field-border,#e5e7eb)] px-3.5 py-3 text-xs text-[var(--ap-form-text,#6b7280)]"
                style={
                    {
                        '--ap-downloads-delay': `${firstTileDelay + count * 90}ms`,
                    } as CSSProperties
                }
            >
                <Mail className="mt-px size-4 shrink-0 opacity-70" />
                <p className="m-0 opacity-80">
                    {email ? (
                        <>
                            A copy of these links is on its way to{' '}
                            <span className="font-medium text-[var(--ap-form-text,#111827)]">
                                {email}
                            </span>
                            .
                        </>
                    ) : (
                        'A copy of these links is on its way to your inbox.'
                    )}
                    {expires ? ` They work until ${expires}.` : null}
                </p>
            </div>

            {compact || !onClose ? null : (
                <div className="flex justify-end">
                    <Button
                        weight="normal"
                        className="rounded-[var(--ap-form-radius,9999px)] border border-[var(--ap-form-field-border,#e5e7eb)] bg-transparent px-6 py-2.5 text-[var(--ap-form-text,#111827)] hover:bg-[var(--ap-form-field-bg,#f3f4f6)]"
                        onClick={onClose}
                    >
                        Done
                    </Button>
                </div>
            )}
        </div>
    );
};
